import React, { createContext, useState, useContext, useEffect } from 'react';
import { encryptedPost, encryptedFetch } from '../utils/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

    useEffect(() => {
        // Check if token exists and validate it
        if (token) {
            // Token validation could be done here, but for simplicity we'll just set user from token
            const storedUser = localStorage.getItem('user');
            if (storedUser && storedUser !== 'null' && storedUser !== 'undefined') {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                } catch (error) {
                    console.error('Error parsing stored user data:', error);
                    // Clear invalid data
                    localStorage.removeItem('user');
                    setUser(null);
                }
            }
        }
        setLoading(false);
    }, [token]);

    const login = async (username, password) => {
        try {
            const data = await encryptedPost(
                `${API_BASE_URL}/auth/login`,
                { username, password }
            );

            setToken(data.token);
            setUser(data.user);
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            return data;
        } catch (error) {
            // Handle decrypted error response
            if (error.message) {
                throw error;
            }
            throw new Error('Login failed');
        }
    };

    const logout = async () => {
        try {
            if (token) {
                await encryptedPost(
                    `${API_BASE_URL}/auth/logout`,
                    {},
                    { 'Authorization': `Bearer ${token}` }
                );
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setToken(null);
            setUser(null);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        }
    };

    /**
     * Make encrypted API request with authentication
     * @param {string} url - API endpoint
     * @param {object} options - Fetch options
     * @param {object} body - Request body
     * @returns {Promise<object>} - Decrypted response
     */
    const encryptedApiCall = async (url, options = {}, body = null) => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            ...options.headers
        };
        return encryptedFetch(url, { ...options, headers }, body);
    };

    const value = {
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token,
        isAdmin: user?.role === 'ADMIN',
        encryptedApiCall,
        API_BASE_URL,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

